import { isFunction } from 'lodash';
import { useMutation as useMutationBase, useQueryClient } from 'react-query';

import useModal from 'hooks/useModal';
import useSnackbar from 'hooks/useSnackbar';
import { isNonEmptyArray, ensureArray } from 'utils/arrays';

const getErrorMessage = ({
  error,
  errorSnackbarMessage,
  shouldUseErrorFromApi,
}) => {
  const isNetworkError = error.errorCode === 'NETWORK_ERROR';

  if ((shouldUseErrorFromApi || isNetworkError) && error?.message) {
    return error?.message;
  }
  return errorSnackbarMessage;
};

/**
 * Use mutation hook
 *
 * @param  {function} apiFunc - function to be called on the mutation
 * @param  {string[]} invalidateQueries - query keys that will be invalidated on the success flow
 * @param  {string | function} successSnackbarMessage - for dynamic messages, we can pass a function that returns a string, or only a static string for the success flow
 * @param  {string} errorSnackbarMessage - snackbar message for the error flow
 * @param {boolean} skipErrorSnackbar - if true, will not show error snackbar
 * @param {boolean} shouldUseErrorFromApi - if true, api error message will be used in snackbar message for the error flow
 * @param  {boolean} closeModalOnSuccess - if true, hides the modal on the success flow
 * @param  {function} onSuccess - success flow callback
 * @param  {function} onError - error flow callback
 * @param  {Object} options - query options
 * @param  {Object} invalidateQueriesOptions - options for invalidating queries
 * @param {function} getErrorMessageFromResponse - function that selects error message from special path instead of hooks default function
 */
const useMutation = ({
  apiFunc,
  invalidateQueries = [],
  successSnackbarMessage = '',
  errorSnackbarMessage = 'Something went wrong.',
  isErrorSnackbarPersistent = true,
  skipErrorSnackbar = false,
  shouldUseErrorFromApi = false,
  closeModalOnSuccess = false,
  onSuccess,
  onError,
  options = {},
  snackbarConfig = {},
  invalidateQueriesOptions = {},
  getErrorMessageFromResponse,
  shouldThrowErrorAsync = false,
} = {}) => {
  const queryClient = useQueryClient();
  const { setSnackbarMessage } = useSnackbar();
  const { hideModal } = useModal();

  const { data, isLoading, mutate, mutateAsync, isSuccess } = useMutationBase(
    params => apiFunc(params),
    {
      onSuccess: (result, variables) => {
        if (isNonEmptyArray(invalidateQueries)) {
          ensureArray(invalidateQueries).forEach(queryKey =>
            queryClient.invalidateQueries(queryKey, {
              ...invalidateQueriesOptions,
            }),
          );
        }
        if (successSnackbarMessage) {
          const { snackbarVariant, snackbarIcon } = snackbarConfig;
          setSnackbarMessage({
            message: isFunction(successSnackbarMessage)
              ? successSnackbarMessage(result, variables)
              : successSnackbarMessage,
            variant: snackbarVariant || 'default',
            icon: snackbarIcon || null,
          });
        }
        if (onSuccess) {
          onSuccess(result, variables);
        }
        if (closeModalOnSuccess) {
          hideModal();
        }
      },
      onError: (error, variables, context) => {
        const hasErrorMessageInitializer = isFunction(
          getErrorMessageFromResponse,
        );
        const message = hasErrorMessageInitializer
          ? getErrorMessageFromResponse(error, variables)
          : getErrorMessage({
              error,
              errorSnackbarMessage,
              shouldUseErrorFromApi,
            });
        if (!skipErrorSnackbar) {
          setSnackbarMessage({
            message,
            variant: 'error',
            persistent: isErrorSnackbarPersistent,
          });
        }
        if (onError) {
          onError(error, variables, context);
        }
      },
      ...options,
    },
  );

  /*
    As guided by react-query documentation:
    https://tanstack.com/query/v4/docs/guides/mutations#promises
    @mutate function catches error by itself but with @mutateAsync, we need to catch errors by ourself
    We used try/catch to handle any response errors that we catch with handleError util.
  */
  const handledMutateAsync = async args => {
    try {
      const response = await mutateAsync(args);
      return response;
    } catch (error) {
      if (isFunction(onError)) {
        onError(error);
      }
      console.error(
        `Request to the BE has been failed:\nError message: ${error?.message}\nError code: ${error?.errorCode}`,
      );

      if (shouldThrowErrorAsync) {
        throw error;
      }

      return { data: error, isError: true };
    }
  };

  return {
    data,
    isLoading,
    isSuccess,
    mutate,
    mutateAsync: handledMutateAsync,
  };
};

export default useMutation;
